import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus,
  Users,
  MoreVertical,
  Trash2,
  UserPlus,
  UserMinus,
  Calendar,
  ArrowLeft,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Groups = () => {
  const { user, userProfile } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showGroupMenu, setShowGroupMenu] = useState(null);

  // Confirmation modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmData, setDeleteConfirmData] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaveConfirmData, setLeaveConfirmData] = useState(null);

  // Form states
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    friendIds: []
  });

  const [inviteForm, setInviteForm] = useState({
    friendIds: []
  });

  const [friends, setFriends] = useState([]);

  // Fetch user's groups
  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const token = await user.getIdToken();
      // Add cache-busting parameter to prevent browser caching
      const cacheBuster = new Date().getTime();
      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/groups/user/${userProfile.user_id}?_t=${cacheBuster}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch groups');
      }

      const data = await response.json();
      setGroups(data.data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [user, userProfile]);

  // Fetch user's friends
  const fetchFriends = useCallback(async () => {
    try {
      const token = await user.getIdToken();
      // Add cache-busting parameter to prevent browser caching
      const cacheBuster = new Date().getTime();
      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/friends/${userProfile.user_id}?_t=${cacheBuster}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch friends');
      }

      const data = await response.json();
      setFriends(data.data || []);
    } catch (error) {
      console.error('Error fetching friends:', error);
      toast.error('Failed to load friends');
    }
  }, [user, userProfile]);

  // Create a new group
  const createGroup = async (e) => {
    e.preventDefault();
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/groups/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createForm)
      });

      if (!response.ok) {
        throw new Error('Failed to create group');
      }

      await response.json();
      toast.success('Group created successfully!');
      setShowCreateModal(false);
      setCreateForm({ name: '', description: '', friendIds: [] });
      fetchGroups(); // Refresh groups list
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group');
    }
  };

  // Invite friends to group
  const inviteFriends = async (e) => {
    e.preventDefault();
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/groups/${selectedGroup.group_id}/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inviteForm)
      });

      if (!response.ok) {
        throw new Error('Failed to invite friends');
      }

      const data = await response.json();
      toast.success(`${data.data.length} invitations sent!`);
      setShowInviteModal(false);
      setInviteForm({ friendIds: [] });
      setSelectedGroup(null);
      fetchGroups(); // Refresh groups list
    } catch (error) {
      console.error('Error inviting friends:', error);
      toast.error('Failed to invite friends');
    }
  };

  // Delete group (admin only)
  const deleteGroup = async (groupId, groupName) => {
    setDeleteConfirmData({ groupId, groupName });
    setDeleteConfirmText('');
    setShowDeleteConfirm(true);
    setShowGroupMenu(null); // Close the group menu
  };

  // Execute delete after confirmation
  const executeDelete = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type "DELETE" exactly to confirm');
      return;
    }

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/groups/${deleteConfirmData.groupId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete group');
      }

      toast.success('Group deleted successfully!');
      setShowDeleteConfirm(false);
      setDeleteConfirmData(null);
      setDeleteConfirmText('');
      fetchGroups(); // Refresh groups list
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  // Leave group
  const leaveGroup = async (groupId, groupName) => {
    setLeaveConfirmData({ groupId, groupName });
    setShowLeaveConfirm(true);
    setShowGroupMenu(null); // Close the group menu
  };

  // Execute leave after confirmation
  const executeLeave = async () => {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5001/api'}/groups/${leaveConfirmData.groupId}/leave`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to leave group');
      }

      toast.success('Left group successfully!');
      setShowLeaveConfirm(false);
      setLeaveConfirmData(null);
      fetchGroups(); // Refresh groups list
    } catch (error) {
      console.error('Error leaving group:', error);
      toast.error('Failed to leave group');
    }
  };

  useEffect(() => {
    if (user && userProfile) {
      fetchGroups();
      fetchFriends();
    }
  }, [user, userProfile, fetchGroups, fetchFriends]);

  // Refresh data when component becomes visible (user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user && userProfile) {
        fetchGroups();
        fetchFriends();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, userProfile, fetchGroups, fetchFriends]);

  // Manual refresh function
  const refreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchGroups(), fetchFriends()]);
      toast.success('Groups refreshed!');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleFriendToggle = (friendId, formType) => {
    if (formType === 'create') {
      setCreateForm(prev => ({
        ...prev,
        friendIds: prev.friendIds.includes(friendId)
          ? prev.friendIds.filter(id => id !== friendId)
          : [...prev.friendIds, friendId]
      }));
    } else {
      setInviteForm(prev => ({
        ...prev,
        friendIds: prev.friendIds.includes(friendId)
          ? prev.friendIds.filter(id => id !== friendId)
          : [...prev.friendIds, friendId]
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link 
              to="/" 
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Your Groups</h1>
              <p className="text-gray-600">Manage your friend groups for planning</p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={refreshData}
              disabled={refreshing}
              className="bg-gray-600 text-white px-4 py-3 rounded-xl font-semibold flex items-center space-x-2 hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold flex items-center space-x-2 hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Create Group</span>
            </button>
          </div>
        </div>

        {/* Groups Grid */}
        {groups.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12"
          >
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Groups Yet</h3>
            <p className="text-gray-600 mb-6">Create your first group to start planning with friends</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              Create Your First Group
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.map((group) => (
              <motion.div
                key={group.group_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-gray-900">{group.name}</h3>
                        {group.role === 'admin' && (
                          <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full font-medium">
                            Admin
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{group.member_count} members</p>
                    </div>
                  </div>
                  
                  <div className="relative">
                    <button
                      onClick={() => setShowGroupMenu(showGroupMenu === group.group_id ? null : group.group_id)}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-400" />
                    </button>
                    
                    {showGroupMenu === group.group_id && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10"
                      >
                        {/* Everyone can create plans */}
                        <Link
                          to={`/generate?groupId=${group.group_id}`}
                          className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Calendar className="w-4 h-4" />
                          <span>Create Plan</span>
                        </Link>
                        
                        {/* Admin-only options */}
                        {group.role === 'admin' && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedGroup(group);
                                setShowInviteModal(true);
                                setShowGroupMenu(null);
                              }}
                              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <UserPlus className="w-4 h-4" />
                              <span>Invite Friends</span>
                            </button>
                            <hr className="my-1" />
                            <button
                              onClick={() => deleteGroup(group.group_id, group.name)}
                              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete Group</span>
                            </button>
                          </>
                        )}
                        
                        {/* Everyone can leave (but admins see it at bottom) */}
                        {group.role !== 'admin' && (
                          <>
                            <hr className="my-1" />
                            <button
                              onClick={() => leaveGroup(group.group_id, group.name)}
                              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <UserMinus className="w-4 h-4" />
                              <span>Leave Group</span>
                            </button>
                          </>
                        )}
                        
                        {/* Admin leave option (at bottom after delete) */}
                        {group.role === 'admin' && (
                          <>
                            <hr className="my-1" />
                            <button
                              onClick={() => leaveGroup(group.group_id, group.name)}
                              className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <UserMinus className="w-4 h-4" />
                              <span>Leave Group</span>
                            </button>
                          </>
                        )}
                      </motion.div>
                    )}
                  </div>
                </div>

                {group.description && (
                  <p className="text-sm text-gray-600 mb-4">{group.description}</p>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Created {new Date(group.created_at).toLocaleDateString()}
                  </span>
                  <Link
                    to={`/generate?groupId=${group.group_id}`}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Plan Together
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Create Group Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl p-6 w-full max-w-md"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Group</h2>
              
              <form onSubmit={createGroup}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter group name"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe your group"
                    rows={3}
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invite Friends (Optional)
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    {friends.map((friend) => (
                      <label key={friend.friend_id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={createForm.friendIds.includes(friend.friend_id)}
                          onChange={() => handleFriendToggle(friend.friend_id, 'create')}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{friend.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Create Group
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Invite Friends Modal */}
        {showInviteModal && selectedGroup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl p-6 w-full max-w-md"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Invite Friends to {selectedGroup.name}
              </h2>
              
              <form onSubmit={inviteFriends}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Friends to Invite
                  </label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-lg p-2">
                    {friends.map((friend) => (
                      <label key={friend.friend_id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={inviteForm.friendIds.includes(friend.friend_id)}
                          onChange={() => handleFriendToggle(friend.friend_id, 'invite')}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{friend.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteModal(false);
                      setSelectedGroup(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviteForm.friendIds.length === 0}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Send Invitations
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-6">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
                  Delete Group Permanently
                </h2>
                
                <div className="text-center mb-6">
                  <p className="text-gray-600 mb-4">
                    Are you sure you want to delete <strong>"{deleteConfirmData?.groupName}"</strong>?
                  </p>
                  
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
                    <p className="text-sm text-red-800 font-medium mb-2">This action will:</p>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• Remove the group permanently</li>
                      <li>• Remove all group members</li>
                      <li>• Delete all group data</li>
                      <li>• <strong>This action CANNOT be undone</strong></li>
                    </ul>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <span className="font-mono bg-gray-100 px-2 py-1 rounded">DELETE</span> to confirm:
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Type DELETE here..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    autoFocus
                  />
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmData(null);
                      setDeleteConfirmText('');
                    }}
                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeDelete}
                    disabled={deleteConfirmText !== 'DELETE'}
                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    Delete Group
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Leave Confirmation Modal */}
        <AnimatePresence>
          {showLeaveConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mx-auto mb-6">
                  <UserMinus className="w-8 h-8 text-orange-600" />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
                  Leave Group
                </h2>
                
                <div className="text-center mb-6">
                  <p className="text-gray-600 mb-4">
                    Are you sure you want to leave <strong>"{leaveConfirmData?.groupName}"</strong>?
                  </p>
                  
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-left">
                    <p className="text-sm text-orange-800 font-medium mb-2">You will:</p>
                    <ul className="text-sm text-orange-700 space-y-1">
                      <li>• Be removed from the group</li>
                      <li>• No longer receive group notifications</li>
                      <li>• Need to be re-invited to rejoin</li>
                      <li>• Keep your friendships with other members</li>
                    </ul>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => {
                      setShowLeaveConfirm(false);
                      setLeaveConfirmData(null);
                    }}
                    className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeLeave}
                    className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                  >
                    Leave Group
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Groups;
